import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import settings from 'popups/Settings'
import { RRule, Options as RRuleOptions } from 'rrule'
import { Attendee } from 'node-ical'
// CalendarService(barrel) 순환 참조 방지를 위해 원본 모듈에서 직접 import
import { getDefaultCalendar, getUserInfo, Calendar } from '../../modules/CalendarState'
import { findCalendarByEventServerId } from '../../modules/EventManager'

import EventInstance from '../../modules/EventInstance'
import { convertToRRuleOptions } from '../../utils/rruleCompat'

dayjs.extend(timezone)
dayjs.extend(utc)

interface CalendarSettings {
  defaultDuration: number
  defaultReminderTime: number
  defaultReminderTimeForAll: number
  lastAddedCalendar?: string
  calendarSyncTargets?: string[]
}

interface UserInfo {
  email: string
  name: string
  personaDomain?: string
}

// VAttendee interface for VEVENT attendees
interface VAttendee {
  val: string
  params: {
    CN: string
    ROLE: 'REQ-PARTICIPANT' | 'OPT-PARTICIPANT' | 'ORGANIZER'
    PARTSTAT: 'NEEDS-ACTION' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE' | null
  }
}

// VAlarm interface for VEVENT alarms
interface VAlarm {
  type: 'VALARM'
  action: 'DISPLAY'
  trigger: string
  description: string
}

// Extended Date type with timezone
interface DateWithTz extends Date {
  tz?: string
}

// Attachment types
interface FileAttachment {
  filename: string
  size: number
  mimeType: string
  url?: string
  file: File
  attachment?: any
}

// Attendee input for new events
interface AttendeeInput {
  name: string
  email: string
}

interface AttendeesInput {
  required?: AttendeeInput[]
  optional?: AttendeeInput[]
}

// Target dates for event creation
interface TargetDates {
  start: Date
  end: Date
}

// Organizer information
interface OrganizerInfo {
  email: string
  name: string
}

// VEvent extension with custom properties
interface ExtendedVEvent {
  type: string
  params: any[]
  uid?: string
  summary?: string
  description?: string
  location?: string
  start: Date
  end: Date
  datetype?: string
  class?: string
  transparency?: string
  color?: string
  status?: string
  sequence?: string
  recurrenceid?: Date
  rrule?: RRule
  'NI-AUTHOR'?: {
    val: string
    params: {
      CN: string
    }
  }
  organizer?: {
    val: string
    params: {
      CN: string
    }
  }
  attendee?: VAttendee[]
  alarms?: VAlarm[]
  exdate?: { [key: string]: Date }
  recurrences?: { [key: string]: any }
  attach?: any[]
}

// Edit mode types
type RecurrenceEditMode = 'all' | 'this' | 'future' | null

// EditAttendee class for managing attendees
class EditAttendee {
  email: string
  name: string
  role: 'REQ-PARTICIPANT' | 'OPT-PARTICIPANT' | 'ORGANIZER'
  response: 'NEEDS-ACTION' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE' | null

  constructor(attendee: VAttendee) {
    this.email = attendee.val.toLowerCase().replace('mailto:', '')
    this.name = attendee.params.CN || ''
    this.role = attendee.params.ROLE || 'OPT-PARTICIPANT'
    this.response = attendee.params.PARTSTAT || null
  }

  toVAttendee(): VAttendee {
    return {
      val: `mailto:${this.email}`,
      params: {
        CN: this.name,
        ROLE: this.role,
        PARTSTAT: this.response,
      },
    }
  }
}

// Main EventData class
export class EventData {
  // Core properties
  id?: string | null
  uid: string
  title: string
  description: string
  location: string
  startDate: Date
  startTz: string
  endDate: Date
  endTz: string
  duration: number
  isAllDay: boolean
  isBusy: boolean
  isPrivate: boolean
  color: string
  calendar: Calendar | null

  // Attendees and organizer
  attendees: EditAttendee[]
  optionalAttendees: EditAttendee[]
  organizer: OrganizerInfo | null

  // Recurrence and alarms
  recurrenceOptions: Partial<RRuleOptions> | null
  alarms: number[] | null

  // Attachments
  attachments: FileAttachment[]
  willDeleteAttachments: any[]

  // Metadata
  author?: UserInfo
  sequence: string
  isMyEvent: boolean

  // Edit mode tracking
  editMode?: RecurrenceEditMode
  orgInstance?: EventInstance
  recurrenceid?: Date

  constructor(
    instance?: EventInstance | null,
    date?: Date | null,
    recurrenceEditMode: RecurrenceEditMode = 'all',
    targetDates: TargetDates | null = null,
    forAllday: boolean = false,
    title: string = '',
    attendees: AttendeesInput | null = null,
  ) {
    if (instance) {
      this.initFromInstance(instance, recurrenceEditMode)
    } else {
      this.initNewEvent(date, targetDates, forAllday, title, attendees)
    }
  }

  private initFromInstance(instance: EventInstance, recurrenceEditMode: RecurrenceEditMode): void {
    const isExceptionEdit = recurrenceEditMode === 'this'
    const isFutureEdit = recurrenceEditMode === 'future'
    const isAllEdit = recurrenceEditMode === 'all' || !recurrenceEditMode

    let _workingInstance = instance

    if (isAllEdit) {
      // 전체 편집시 진입한 이벤트가 처음 이벤트가 아닐 수 있고 익셉션을 무시하기 위해 복사 재생성.
      _workingInstance = new EventInstance(
        instance.serverId,
        instance.etag,
        instance.color,
        instance.event,
      )
      this.editMode = 'all'
    } else if (isExceptionEdit || isFutureEdit) {
      this.editMode = recurrenceEditMode
      if (isExceptionEdit) {
        if (_workingInstance.exception?.recurrenceid) {
          this.recurrenceid = _workingInstance.exception.recurrenceid as Date
        } else {
          const d = new Date(_workingInstance.startDate) as DateWithTz
          d.tz = _workingInstance.startTz
          this.recurrenceid = d
        }
      }
    }
    this.orgInstance = _workingInstance

    if (isFutureEdit) {
      this.uid = crypto.randomUUID()
    } else {
      this.uid = _workingInstance.event!.uid!
    }
    if (_workingInstance.author) {
      this.author = {
        email: _workingInstance.author.val,
        name: _workingInstance.author.params.CN,
      }
    }
    this.title = _workingInstance.subject
    this.description = _workingInstance.description
    this.location = _workingInstance.location
    this.isAllDay = _workingInstance.allday
    this.startDate = _workingInstance.startDate
    this.startTz = _workingInstance.startTz

    if (this.isAllDay) {
      if (_workingInstance.endDate.valueOf() !== _workingInstance.startDate.valueOf()) {
        const endDate = new Date(_workingInstance.endDate.getTime() - 1000)
        endDate.setHours(0, 0, 0, 0)
        this.endDate = endDate
        this.duration = (this.endDate.getTime() - this.startDate.getTime()) / 60000 // min
      } else {
        this.endDate = _workingInstance.endDate
        this.duration = 0
      }
    } else {
      this.endDate = _workingInstance.endDate
      this.duration =
        (_workingInstance.endDate.getTime() - _workingInstance.startDate.getTime()) / 60000 // min
    }

    this.endTz = this.startTz
    this.isBusy = _workingInstance.isBusy
    this.isPrivate = _workingInstance.isPrivate
    this.color = _workingInstance.color
    this.calendar = this.findCalendar(_workingInstance.serverId)
    this.attendees = []
    this.optionalAttendees = []

    if (_workingInstance.isRecurring) {
      this.recurrenceOptions = convertToRRuleOptions(
        _workingInstance.recurrenceRule.options as unknown as Record<string, unknown>,
      )
    } else {
      this.recurrenceOptions = null
    }

    _workingInstance.attendees?.forEach((attendee: Attendee) => {
      const editAttendee = new EditAttendee(attendee as VAttendee)
      if (editAttendee.role === 'REQ-PARTICIPANT') {
        this.attendees.push(editAttendee)
      } else if (editAttendee.role !== 'ORGANIZER') {
        this.optionalAttendees.push(editAttendee)
      }
    })

    this.alarms = _workingInstance.alarms || []
    this.alarms = this.alarms.sort((a, b) => b - a)

    if (_workingInstance.organizer) {
      if (typeof _workingInstance.organizer === 'string') {
        const email = _workingInstance.organizer.toLowerCase().replace('mailto:', '')
        this.organizer = {
          email: email,
          name: _workingInstance.organizerName || email.split('@')[0] || '',
        }
      } else {
        const organizerObj = _workingInstance.organizer as any
        const email = organizerObj.val.toLowerCase().replace('mailto:', '')
        this.organizer = {
          email: email,
          name:
            organizerObj.params?.CN || _workingInstance.organizerName || email.split('@')[0] || '',
        }
      }
    } else {
      this.organizer = null
    }

    this.attachments = (_workingInstance.attachments || []) as FileAttachment[]
    this.willDeleteAttachments = []

    this.sequence = String(_workingInstance.event!.sequence ?? 0)
    this.isMyEvent = _workingInstance.isMyEvent
  }

  private initNewEvent(
    date: Date | null | undefined,
    targetDates: TargetDates | null = null,
    forAllday: boolean = false,
    title: string = '',
    attendees: AttendeesInput | null = null,
  ): void {
    let startDate: Date
    let endDate: Date
    const calendarSettings = settings.getValue('calendar') as CalendarSettings

    if (targetDates) {
      startDate = new Date(targetDates.start)
      endDate = new Date(targetDates.end)
    } else {
      let workingDate: Date
      if (!date) {
        workingDate = new Date()
      } else {
        workingDate = new Date(date)
      }
      workingDate.setSeconds(0)
      workingDate.setMilliseconds(0)

      if (forAllday) {
        workingDate.setHours(0)
        workingDate.setMinutes(0)

        this.duration = 24 * 60
        startDate = workingDate
        endDate = new Date(workingDate)
      } else {
        const now = new Date()
        if (
          workingDate.getFullYear() === now.getFullYear() &&
          workingDate.getMonth() === now.getMonth() &&
          workingDate.getDate() === now.getDate()
        ) {
          if (now.getMinutes() >= 30) {
            workingDate.setMinutes(0)
            workingDate.setHours(now.getHours() + 1)
          } else {
            workingDate.setMinutes(30)
            workingDate.setHours(now.getHours())
          }
        } else {
          workingDate.setMinutes(0)
          workingDate.setHours(9)
        }

        startDate = workingDate

        this.duration = calendarSettings.defaultDuration
        endDate = dayjs(startDate).add(this.duration, 'minutes').toDate()
      }
    }

    const defaultCalendar = getDefaultCalendar() as Calendar
    const localtz = dayjs.tz.guess()

    this.id = null
    this.title = title
    this.description = ''
    this.location = ''
    this.uid = crypto.randomUUID()
    this.startDate = startDate
    this.startTz = localtz
    this.endDate = endDate
    this.endTz = localtz
    this.isAllDay = forAllday
    this.isBusy = forAllday ? false : true
    this.isPrivate = false
    this.color = (defaultCalendar?.calendarColor as any)?._cdata || '#000000'
    this.calendar = defaultCalendar
    this.recurrenceOptions = null

    const defaultReminderTime = forAllday
      ? calendarSettings.defaultReminderTimeForAll
      : calendarSettings.defaultReminderTime
    this.alarms = defaultReminderTime === 1 ? null : [defaultReminderTime]
    this.organizer = null
    this.isMyEvent = true
    this.attachments = []
    this.willDeleteAttachments = []

    if (attendees?.required?.length) {
      this.attendees = attendees.required.map((attendee) => {
        return new EditAttendee({
          val: attendee.email,
          params: { CN: attendee.name, ROLE: 'REQ-PARTICIPANT', PARTSTAT: 'NEEDS-ACTION' },
        })
      })
    } else {
      this.attendees = []
    }

    if (attendees?.optional?.length) {
      this.optionalAttendees = attendees.optional.map((attendee) => {
        return new EditAttendee({
          val: attendee.email,
          params: { CN: attendee.name, ROLE: 'OPT-PARTICIPANT', PARTSTAT: 'NEEDS-ACTION' },
        })
      })
    } else {
      this.optionalAttendees = []
    }

    this.sequence = '0'
  }

  findCalendar(serverId: string): Calendar | null {
    if (!serverId) return getDefaultCalendar() as Calendar
    return findCalendarByEventServerId(serverId) as Calendar
  }

  toEventInstance(): EventInstance | null {
    // EventInstance 생성 로직은 추후 구현
    return null
  }

  isSameCalendar(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false
    return this.calendar?.url === baseEventData.calendar?.url
  }

  isChangedColor(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false

    const calendarColor = (this.calendar?.calendarColor as any)?._cdata

    return (
      this.color !== baseEventData.color &&
      (this.color !== calendarColor || // 칼렌다 컬러랑 다른 경우.
        (this.color === calendarColor && baseEventData.color !== calendarColor)) // 칼렌다 컬러랑 동일하게 맞추는 경우.
    )
  }

  isSameContent(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false

    if (this.isChangedColor(baseEventData)) {
      return false
    }

    return (
      this.title === baseEventData.title &&
      this.description === baseEventData.description &&
      this.location === baseEventData.location &&
      this.startDate.getTime() === baseEventData.startDate.getTime() &&
      this.endDate.getTime() === baseEventData.endDate.getTime() &&
      this.startTz === baseEventData.startTz &&
      this.endTz === baseEventData.endTz &&
      this.isAllDay === baseEventData.isAllDay &&
      this.isBusy === baseEventData.isBusy &&
      this.isPrivate === baseEventData.isPrivate &&
      JSON.stringify(this.attendees) === JSON.stringify(baseEventData.attendees) &&
      JSON.stringify(this.optionalAttendees) === JSON.stringify(baseEventData.optionalAttendees) &&
      JSON.stringify(this.alarms) === JSON.stringify(baseEventData.alarms) &&
      JSON.stringify(this.recurrenceOptions ?? {}) ===
        JSON.stringify(baseEventData.recurrenceOptions ?? {})
    )
  }

  isSameAttachments(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false
    return JSON.stringify(this.attachments) === JSON.stringify(baseEventData.attachments)
  }

  isOnlyAttachmentsChanged(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false
    return (
      this.isSameContent(baseEventData) &&
      this.isSameCalendar(baseEventData) &&
      !this.isSameAttachments(baseEventData)
    )
  }

  isOnlyCalendarChanged(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false

    return (
      this.isSameContent(baseEventData) &&
      this.isSameAttachments(baseEventData) &&
      !this.isSameCalendar(baseEventData)
    )
  }

  // 두 이벤트 데이터가 동일한지 비교
  isEqual(baseEventData: EventData | null): boolean {
    if (!baseEventData) return false

    if (!this.isSameCalendar(baseEventData)) {
      return false
    }

    if (!this.isSameAttachments(baseEventData)) {
      return false
    }

    return this.isSameContent(baseEventData)
  }

  // 변경된 내용이 있는지 확인
  hasChanges(baseEventData: EventData | null): boolean {
    return !this.isEqual(baseEventData)
  }

  // Getter/Setter 메서드들
  getTitle(): string {
    return this.title
  }

  setTitle(title: string): void {
    this.title = title
  }

  getDescription(): string {
    return this.description
  }

  setDescription(description: string): void {
    this.description = description
  }

  getLocation(): string {
    return this.location
  }

  setLocation(location: string): void {
    this.location = location
  }

  getStartDate(): Date {
    return this.startDate
  }

  setStartDate(date: Date): void {
    this.startDate = date
  }

  getEndDate(): Date {
    return this.endDate
  }

  setEndDate(date: Date): void {
    this.endDate = date
  }

  isAllDayEvent(): boolean {
    return this.isAllDay
  }

  setAllDay(allDay: boolean): void {
    this.isAllDay = allDay
  }

  getAttendees(): EditAttendee[] {
    return this.attendees
  }

  setAttendees(attendees: EditAttendee[]): void {
    this.attendees = attendees
  }

  getOptionalAttendees(): EditAttendee[] {
    return this.optionalAttendees
  }

  setOptionalAttendees(attendees: EditAttendee[]): void {
    this.optionalAttendees = attendees
  }

  getAlarms(): number[] | null {
    return this.alarms
  }

  setAlarms(alarms: number[] | null): void {
    this.alarms = alarms
  }

  getCalendar(): Calendar | null {
    return this.calendar
  }

  setCalendar(calendar: Calendar): void {
    this.calendar = calendar
  }

  getColor(): string {
    return this.color
  }

  setColor(color: string): void {
    this.color = color
  }

  getOrganizer(): OrganizerInfo | null {
    return this.organizer
  }

  isBusyStatus(): boolean {
    return this.isBusy
  }

  setBusyStatus(busy: boolean): void {
    this.isBusy = busy
  }

  isPrivateEvent(): boolean {
    return this.isPrivate
  }

  setPrivateEvent(isPrivate: boolean): void {
    this.isPrivate = isPrivate
  }

  getRecurrenceOptions(): Partial<RRuleOptions> | null {
    return this.recurrenceOptions
  }

  setRecurrenceOptions(options: Partial<RRuleOptions> | null): void {
    this.recurrenceOptions = options
  }

  addAttachment(file: File): void {
    if (!file) return
    const attachment: FileAttachment = {
      filename: file.name,
      size: file.size,
      mimeType: file.type,
      url: (file as any).url,
      file: file,
    }
    this.attachments = [...this.attachments, attachment]
  }

  deleteAttachment(attachment: FileAttachment): void {
    if (!attachment) return
    if (!this.attachments.includes(attachment)) return
    this.attachments = this.attachments.filter((a) => a !== attachment)
    if (attachment.attachment) {
      this.willDeleteAttachments = [...this.willDeleteAttachments, attachment.attachment]
    }
  }

  willAddAttachments(): FileAttachment[] {
    return this.attachments.filter((a) => !a.attachment)
  }

  private _createBaseVEvent(): ExtendedVEvent {
    const startTz = this.isAllDay ? 'Etc/UTC' : this.startTz
    const endTz = this.isAllDay ? 'Etc/UTC' : this.endTz
    const startDate = dayjs(this.startDate.toISOString()).tz(startTz)
    let endDate = dayjs(this.endDate.toISOString()).tz(endTz)

    if (this.isAllDay) {
      endDate = endDate.add(1, 'day')
    }

    console.log('startDate : %o', startDate)
    console.log('endDate : %o', endDate)

    const s = new Date(startDate.toISOString()) as DateWithTz
    s.tz = startTz
    const e = new Date(endDate.toISOString()) as DateWithTz
    e.tz = endTz

    let color: string | null = null
    if (this.color !== (this.calendar?.calendarColor as any)?._cdata) {
      color = this.color
    }

    const vevent: ExtendedVEvent = {
      type: 'VEVENT',
      params: [],
      uid: this.uid || undefined,
      summary: this.title,
      description: this.description || undefined,
      location: this.location || undefined,
      start: s,
      end: e,
      datetype: this.isAllDay ? 'date' : 'date-time',
      class: this.isPrivate ? 'PRIVATE' : 'PUBLIC',
      transparency: this.isBusy ? 'OPAQUE' : 'TRANSPARENT',
      color: color || undefined,
    } as ExtendedVEvent

    const userInfo = getUserInfo()
    if (userInfo) {
      vevent['NI-AUTHOR'] = {
        val: userInfo.email,
        params: {
          CN: userInfo.name,
        },
      }
    }

    // Set attendees
    if (this.attendees.length > 0 || this.optionalAttendees.length > 0) {
      vevent.attendee = [
        ...this.attendees.map((attendee) => ({
          val: `mailto:${attendee.email}`,
          params: {
            CN: attendee.name,
            ROLE: 'REQ-PARTICIPANT' as const,
            PARTSTAT: attendee.response || 'NEEDS-ACTION',
          },
        })),
        ...this.optionalAttendees.map((attendee) => ({
          val: `mailto:${attendee.email}`,
          params: {
            CN: attendee.name,
            ROLE: 'OPT-PARTICIPANT' as const,
            PARTSTAT: attendee.response || 'NEEDS-ACTION',
          },
        })),
      ]
    }
    vevent.attendee = vevent.attendee || []

    // Set alarms
    if (this.alarms?.length) {
      vevent.alarms = this.alarms.map((seconds) => ({
        type: 'VALARM',
        action: 'DISPLAY',
        trigger: seconds < 0 ? `-PT${Math.abs(seconds / 60)}M` : `PT${seconds / 60}M`,
        description: 'Reminder',
      }))
    }

    return vevent
  }

  private _toException(): ExtendedVEvent {
    const vevent = this._createBaseVEvent()
    vevent.recurrenceid = this.recurrenceid
    return vevent
  }

  private _toFuture(): ExtendedVEvent {
    const vevent = this._createBaseVEvent()
    vevent.status = 'CONFIRMED'
    vevent.sequence = '0'

    // Set organizer
    if (this.organizer) {
      vevent.organizer = {
        val: `mailto:${this.organizer.email}`,
        params: {
          CN: this.organizer.name,
        },
      }
    }

    if (vevent.attendee && vevent.attendee.length > 0 && !vevent.organizer) {
      const owner = getUserInfo()
      let email: string
      let name: string

      if (this.calendar?.owner === owner.email) {
        email = owner.email
        name = owner.name
      } else {
        email = this.calendar?.owner || owner.email
        name = email.split('@')[0]
      }

      vevent.organizer = {
        val: `mailto:${email}`,
        params: {
          CN: name,
        },
      }
    }

    // Set recurrence rule
    if (this.recurrenceOptions) {
      const options = { ...this.recurrenceOptions }
      if (options.until) {
        let newUntil = new Date(options.until)
        newUntil.setHours(23)
        newUntil.setMinutes(59)
        newUntil.setSeconds(59)
        newUntil.setMilliseconds(0)
        options.until = newUntil
      }
      vevent.rrule = new RRule(options as Partial<RRuleOptions>)
    }

    return vevent
  }

  toVEvent(): ExtendedVEvent {
    if (this.editMode === 'this') {
      return this._toException()
    } else if (this.editMode === 'future') {
      return this._toFuture()
    }

    const vevent = this._createBaseVEvent()

    // Add additional fields for regular event
    vevent.status = 'CONFIRMED'
    vevent.sequence = this.sequence
    vevent.exdate = this.orgInstance?.event?.exdate
    vevent.recurrences = this.orgInstance?.event?.recurrences as any

    // Set organizer
    if (this.organizer) {
      vevent.organizer = {
        val: `mailto:${this.organizer.email}`,
        params: {
          CN: this.organizer.name,
        },
      }
    }

    if (vevent.attendee && vevent.attendee.length > 0 && !vevent.organizer) {
      const owner = getUserInfo()
      let email: string
      let name: string

      if (this.calendar?.owner === owner.email) {
        email = owner.email
        name = owner.name
      } else {
        email = this.calendar?.owner || owner.email
        name = email.split('@')[0]
      }

      vevent.organizer = {
        val: `mailto:${email}`,
        params: {
          CN: name,
        },
      }
    }

    // Set recurrence rule
    if (this.recurrenceOptions) {
      const options = { ...this.recurrenceOptions }
      if (options.until) {
        let newUntil = new Date(options.until)
        newUntil.setHours(23)
        newUntil.setMinutes(59)
        newUntil.setSeconds(59)
        newUntil.setMilliseconds(0)
        options.until = newUntil
      }
      vevent.rrule = new RRule(options as Partial<RRuleOptions>)
    }

    if (this.attachments.length > 0) {
      vevent.attach = this.attachments.filter((a) => a.attachment).map((a) => a.attachment)
    }

    return vevent
  }
}

export default EventData
